import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章不存在</h1>
            <Link to="/" className="text-accent-600 hover:text-accent-700">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 4);

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb + Title */}
        <section className="w-full pt-28 pb-8 md:pt-32 md:pb-12 bg-background-50">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </section>

        {/* Article Main Content - Dark Card Layout */}
        <section className="w-full pb-16 md:pb-24 bg-background-50">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="relative bg-foreground-900 rounded-2xl md:rounded-3xl overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                {/* Left: Cover Image */}
                <div className="lg:w-[45%] relative">
                  <div className="aspect-[4/5] lg:aspect-auto lg:h-full overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Right: Content */}
                <div className="lg:w-[55%] p-6 md:p-10 lg:p-14 flex flex-col justify-center">
                  {/* Category Tag */}
                  <span className="inline-block px-3 py-1 bg-accent-500/20 text-accent-400 text-xs rounded-full mb-4 w-fit">
                    {article.category}
                  </span>

                  {/* Title */}
                  <h1
                    className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-background-50 leading-tight mb-6"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {article.title}
                  </h1>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-4 text-background-400 text-sm mb-8">
                    <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                    <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                    <span><i className="ri-eye-line mr-1"></i>{(article.viewCount / 10000).toFixed(1)}万阅读</span>
                  </div>

                  {/* Body */}
                  <div
                    className="prose prose-invert max-w-none text-background-200 text-base leading-relaxed space-y-4"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  {/* Tags */}
                  <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-foreground-800">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-foreground-800 text-background-300 text-xs rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Share */}
                  <div className="flex items-center gap-3 mt-6">
                    <span className="text-background-400 text-sm">分享：</span>
                    <button className="w-9 h-9 flex items-center justify-center bg-foreground-800 text-background-300 rounded-full hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="分享到微博">
                      <i className="ri-weibo-line"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center bg-foreground-800 text-background-300 rounded-full hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="分享到微信">
                      <i className="ri-wechat-line"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center bg-foreground-800 text-background-300 rounded-full hover:bg-accent-500 hover:text-background-50 transition-colors cursor-pointer" aria-label="复制链接">
                      <i className="ri-link"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full py-16 md:py-24 bg-background-100">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-12 mb-10">
                <div className="md:w-[380px] flex-shrink-0">
                  <h2
                    className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    同类大瓜
                    <br />
                    继续吃瓜
                  </h2>
                </div>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-md pt-2">
                  同分类下的更多精彩爆料，让你一次吃个够。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* App Download CTA */}
        <section className="relative w-full py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Smartphone%20displaying%20news%20feed%20on%20dark%20warm%20gradient%20background%2C%20luxury%20tech%20lifestyle%2C%20coral%20and%20charcoal%20color%20palette%2C%20dramatic%20cinematic%20lighting%2C%20professional%20product%20photography%2C%20no%20text%2C%20minimalist%20composition&width=1800&height=700&seq=cta-app-article-bottom&orientation=landscape"
              alt="52吃瓜网App下载"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/40"></div>
          </div>
          <div className="relative z-10 max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <h2
                className="text-2xl md:text-4xl font-heading font-bold text-background-50"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                下载 52吃瓜网 App，大瓜不错过
              </h2>
              <div className="flex flex-col items-end gap-4">
                <p className="text-background-200 text-sm md:text-base text-right">
                  推送通知秒速提醒，随时随地掌握最新吃瓜动态
                </p>
                <a
                  href="#"
                  className="whitespace-nowrap inline-flex items-center gap-3 px-7 py-3.5 bg-background-50 text-foreground-900 rounded-full text-sm font-medium hover:bg-background-100 transition-all hover:scale-105 cursor-pointer"
                >
                  立即下载
                  <span className="w-7 h-7 flex items-center justify-center bg-foreground-900 text-background-50 rounded-full">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Article',
                headline: article.title,
                description: article.excerpt,
                author: {
                  '@type': 'Person',
                  name: article.author,
                },
                datePublished: article.publishDate,
                image: article.coverImage,
                url: `https://52chigua.com/article/${article.id}`,
                publisher: {
                  '@type': 'Organization',
                  name: '52吃瓜网',
                },
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': `https://52chigua.com/article/${article.id}`,
                },
              },
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://52chigua.com',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: article.category,
                    item: `https://52chigua.com/category/${article.categorySlug}`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: article.title,
                  },
                ],
              },
            ],
          }),
        }}
      />
    </>
  );
}